/**
 * Hook React pour gérer un verrou pessimiste sur une ressource.
 *
 * Usage :
 *   const { lockState } = useEditLock('devis', devisId, open);
 *
 *   if (lockState.blocked) {
 *     return <LockedByOtherModal name={lockState.user_name} onClose={...} />;
 *   }
 *
 * Retourne :
 *   - state = 'acquiring' | 'owned' | 'blocked' | 'released'
 *   - blocked: bool
 *   - user_name: string (si blocked)
 *   - expires_at: string (ISO)
 *   - release(): libère explicitement
 *
 * Comportement :
 *   - Tente d'acquérir le lock quand `enabled` passe à true
 *   - Ping toutes les 60s pour maintenir
 *   - Relâche automatiquement à l'unmount ou quand enabled passe à false
 */
import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;
const PING_INTERVAL_MS = 60_000;
const RETRY_WHEN_BLOCKED_MS = 5_000; // Retry auto quand on est bloqué → récupère le lock dès que l'autre user libère

export function useEditLock(resourceType, resourceId, enabled = true) {
    const [state, setState] = useState('released'); // 'acquiring' | 'owned' | 'blocked' | 'released'
    const [blockedBy, setBlockedBy] = useState(null); // { user_name, user_id, expires_at }
    const pingIntervalRef = useRef(null);
    const retryTimeoutRef = useRef(null);

    const clearTimers = useCallback(() => {
        if (pingIntervalRef.current) { clearInterval(pingIntervalRef.current); pingIntervalRef.current = null; }
        if (retryTimeoutRef.current) { clearTimeout(retryTimeoutRef.current); retryTimeoutRef.current = null; }
    }, []);

    const release = useCallback(async () => {
        try {
            await axios.delete(`${API}/api/locks/${resourceType}/${resourceId}`, { withCredentials: true });
        } catch {}
        clearTimers();
        setState('released');
        setBlockedBy(null);
    }, [resourceType, resourceId, clearTimers]);

    const acquire = useCallback(async () => {
        setState((prev) => prev === 'blocked' ? 'blocked' : 'acquiring');
        try {
            await axios.post(`${API}/api/locks/${resourceType}/${resourceId}/acquire`, {}, { withCredentials: true });
            clearTimers();
            setState('owned');
            setBlockedBy(null);
            // Start ping interval to keep the lock alive
            pingIntervalRef.current = setInterval(async () => {
                try {
                    await axios.post(`${API}/api/locks/${resourceType}/${resourceId}/ping`, {}, { withCredentials: true });
                } catch (err) {
                    if (err?.response?.status === 423 || err?.response?.status === 409) {
                        clearTimers();
                        const detail = err.response?.data?.detail || {};
                        setState('blocked');
                        setBlockedBy({
                            user_name: detail.user_name || 'un autre utilisateur',
                            user_id: detail.user_id,
                            expires_at: detail.expires_at,
                        });
                        // Enclenche le retry pour récupérer automatiquement
                        scheduleRetry();
                    }
                }
            }, PING_INTERVAL_MS);
        } catch (err) {
            if (err?.response?.status === 423) {
                const detail = err.response?.data?.detail || {};
                setState('blocked');
                setBlockedBy({
                    user_name: detail.user_name || 'un autre utilisateur',
                    user_id: detail.user_id,
                    expires_at: detail.expires_at,
                });
                // Retente dans 5s — dès que l'autre user ferme, on récupère automatiquement le lock
                scheduleRetry();
            } else {
                setState('released');
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceType, resourceId, clearTimers]);

    const scheduleRetry = useCallback(() => {
        if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = setTimeout(() => {
            acquire();
        }, RETRY_WHEN_BLOCKED_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!enabled || !resourceId) {
            clearTimers();
            release();
            return;
        }
        acquire();
        return () => {
            clearTimers();
            // Best-effort release on unmount
            axios.delete(`${API}/api/locks/${resourceType}/${resourceId}`, { withCredentials: true }).catch(() => {});
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, resourceId, resourceType]);

    // beforeunload : release quand on ferme l'onglet (best-effort via fetch keepalive)
    useEffect(() => {
        if (!enabled || !resourceId) return undefined;
        const handler = () => {
            try {
                const url = `${API}/api/locks/${resourceType}/${resourceId}`;
                fetch(url, { method: 'DELETE', credentials: 'include', keepalive: true }).catch(() => {});
            } catch {}
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [enabled, resourceId, resourceType]);

    return {
        state,
        blocked: state === 'blocked',
        owned: state === 'owned',
        acquiring: state === 'acquiring',
        user_name: blockedBy?.user_name,
        expires_at: blockedBy?.expires_at,
        release,
    };
}
